// BOORO: what this one agent may do without asking again.
import { approvalToolKind, readableToolName } from "@booro/tasks";
import { Trans, useLingui } from "@lingui/react/macro";
import type { AgentApprovalRule } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

/** The rules of one agent, with a way to remove each of them. */
export function AgentApprovalRules({ botId }: { botId: string }) {
  const { t } = useLingui();
  const kindLabels = {
    email: t`Send emails`,
    calendar: t`Change calendar entries`,
    payment: t`Make payments`,
    page: t`Change the handbook`,
  };
  const [rules, setRules] = useState<AgentApprovalRule[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void rpc.booroApprovals
      .listAgentRules({ botId })
      .then((next) => {
        if (active) setRules(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [botId]);

  if (rules.length === 0) return null;

  return (
    <section data-testid="agent-approval-rules" className="mt-5">
      <h3 className="text-[13px] font-semibold text-foreground">
        <Trans>Allowed without asking</Trans>
      </h3>
      <ul className="mt-2 space-y-1.5">
        {rules.map((rule) => {
          // A known kind reads as the action; the tool name stays below for the details.
          const kind = approvalToolKind(rule.matchValue);
          const toolName = readableToolName(rule.matchValue);
          return (
            <li key={rule.id} className="flex items-center justify-between gap-3 text-[13.5px]">
              <span className="min-w-0" dir="auto">
                <span className="block truncate text-foreground/85">
                  {kind === "generic" ? toolName : kindLabels[kind]}
                </span>
                {kind === "generic" ? null : (
                  <span className="block truncate text-[12px] text-muted-foreground">
                    {toolName}
                  </span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError("");
                  try {
                    await rpc.booroApprovals.removeAgentRule({ ruleId: rule.id });
                    setRules((previous) => previous.filter((entry) => entry.id !== rule.id));
                  } catch (cause) {
                    setError(errorText(cause));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <Trans>Remove</Trans>
              </Button>
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="mt-2 text-[13px] text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
